import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, toast } from "../App";
import { Edit, Trash2, Plus } from "lucide-react";
import BulkImportPreview from "@/components/BulkImportPreview";

const DEPARTMENTS = [
  "Management",
  "Sales",
  "Estimation",
  "Projects",
  "PMO",
  "Training",
  "Human Resources",
  "Accounts and Finance",
  "Quality Assurance"
];

export default function Employees({ user }) {
  const [employees, setEmployees] = useState([]);
  const [archivedEmployees, setArchivedEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState("active"); // "active" | "archived"
  const [teams, setTeams] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: "",
    first_name: "",
    last_name: "",
    pseudo_first_name: "",
    pseudo_last_name: "",
    department: "Projects",
    access_level: "Team Member",
    branch: "",
    team_id: "",
    dob: "",
    intern_id: null,
    manager_id: "",
    email: "",
    gender: "",
    employment_status: "Current",
    resignation_date: "",
    last_working_day: "",
    joining_date: ""
  });
  const [internDetails, setInternDetails] = useState(null);

  useEffect(() => {
    fetchEmployees();
    fetchTeams();
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await api.get("/branches");
      setBranches(response.data || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
      setBranches([]);
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const [activeRes, archivedRes] = await Promise.all([
        api.get("/employees?status=active"),
        api.get("/employees?status=archived")
      ]);
      setEmployees(activeRes.data);
      setArchivedEmployees(archivedRes.data);
    } catch (error) {
      toast.error("Failed to fetch employees");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  const fetchTeams = async () => {
    try {
      const response = await api.get("/teams");
      setTeams(response.data);
    } catch (error) {
      console.error("Failed to fetch teams:", error);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for duplicate employee_id
    const isDuplicate = employees.some(emp => {
      // In edit mode, exclude the current employee being edited
      if (editMode && emp.id === editingEmployeeId) {
        return false;
      }
      return emp.employee_id === formData.employee_id;
    });
    
    if (isDuplicate) {
      toast.error(`Employee ID "${formData.employee_id}" already exists. Please use a unique Employee ID.`);
      return;
    }
    
    try {
      if (editMode) {
        await api.put(`/employees/${editingEmployeeId}`, formData);
        toast.success("Employee updated successfully");
      } else {
        await api.post("/employees", formData);
        toast.success("Employee created successfully");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = `Failed to ${editMode ? 'update' : 'create'} employee`;
      
      if (errorDetail) {
        if (typeof errorDetail === 'string') {
          errorMessage = errorDetail;
        } else if (Array.isArray(errorDetail)) {
          errorMessage = errorDetail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof errorDetail === 'object') {
          errorMessage = JSON.stringify(errorDetail);
        }
      }
      
      toast.error(errorMessage);
      console.error("Employee save error:", error.response?.data);
    }
  };

  const fetchInternDetails = async (internId) => {
    try {
      const response = await api.get(`/interns/${internId}`);
      setInternDetails(response.data);
    } catch (error) {
      console.error("Failed to fetch intern details:", error);
      setInternDetails(null);
    }
  };

  const handleEdit = async (employee) => {
    setEditMode(true);
    setEditingEmployeeId(employee.id);
    setFormData({
      employee_id: employee.employee_id,
      first_name: employee.first_name,
      last_name: employee.last_name,
      pseudo_first_name: employee.pseudo_first_name || "",
      pseudo_last_name: employee.pseudo_last_name || "",
      department: employee.department || "Projects",
      access_level: employee.access_level || "Team Member",
      branch: employee.branch || "",
      team_id: employee.team_id || "",
      dob: employee.dob,
      intern_id: employee.intern_id || null,
      manager_id: employee.manager_id || "",
      email: employee.email || "",
      gender: employee.gender || "",
      employment_status: employee.employment_status || "Current",
      resignation_date: employee.resignation_date || "",
      last_working_day: employee.last_working_day || "",
      joining_date: employee.joining_date || ""
    });
    
    // Fetch intern details if employee was converted from intern
    if (employee.intern_id) {
      await fetchInternDetails(employee.intern_id);
    } else {
      setInternDetails(null);
    }
    
    setDialogOpen(true);
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm("Are you sure you want to delete this employee?")) {
      return;
    }

    try {
      await api.delete(`/employees/${employeeId}`);
      toast.success("Employee deleted successfully");
      fetchEmployees();
    } catch (error) {
      toast.error("Failed to delete employee");
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: "",
      first_name: "",
      last_name: "",
      pseudo_first_name: "",
      pseudo_last_name: "",
      department: "Projects",
      access_level: "Team Member",
      branch: "",
      team_id: "",
      dob: "",
      intern_id: null,
      manager_id: "",
      email: "",
      gender: "",
      employment_status: "Current",
      resignation_date: "",
      last_working_day: "",
      joining_date: ""
    });
    setInternDetails(null);
    setEditMode(false);
    setEditingEmployeeId(null);
  };

  const handleDepartmentChange = (department) => {
    const updates = { department };
    
    // If Management is selected, clear the branch field
    if (department === "Management") {
      updates.branch = "";
    } else {
      // For other departments, keep or set default branch
      updates.branch = formData.branch || "";
      
      // If switching away from Management and access_level is "Management", reset it
      if (formData.access_level === "Management") {
        updates.access_level = "Team Member";
      }
    }
    
    setFormData({ ...formData, ...updates });
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  // Bulk import with preview states
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState(null);
  
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/employees/template/download', {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employee_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Template downloaded successfully");
    } catch (error) {
      toast.error("Failed to download template");
      console.error(error);
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
    setUploadResult(null);
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      // Call preview endpoint
      const response = await api.post('/import/employees/preview', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportPreviewData(response.data);
      setImportPreviewOpen(true);
      setBulkUploadDialogOpen(false);
      setSelectedFile(null);
      
      setSelectedFile(null);
      
    } catch (error) {
      toast.error("Failed to upload file: " + (error.response?.data?.detail || error.message));
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleApplyImport = async (applyData) => {
    try {
      const response = await api.post("/import/employees/apply", applyData);
      
      toast.success(`Successfully imported ${response.data.imported} and updated ${response.data.updated} employees!`);
      
      setImportPreviewOpen(false);
      fetchEmployees();
    } catch (error) {
      toast.error("Failed to apply import");
    }
  };

  return (
    <Layout user={user}>

      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-md3-display-small">Employees</h1>
          <div className="flex gap-2">
            <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setBulkUploadDialogOpen(true)}>
              Bulk Upload
            </Button>
            {activeTab === "active" && (
              <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={openCreateDialog}>
                <Plus className="mr-2 h-4 w-4" /> Add Employee
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 border-b">
          <button
            className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${activeTab === "active" ? "bg-white border border-b-white text-blue-600 border-gray-200" : "text-gray-500 hover:text-gray-700"}`}
            onClick={() => setActiveTab("active")}
            data-testid="tab-active-employees"
          >
            Active Employees ({employees.length})
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${activeTab === "archived" ? "bg-white border border-b-white text-red-600 border-gray-200" : "text-gray-500 hover:text-gray-700"}`}
            onClick={() => setActiveTab("archived")}
            data-testid="tab-archived-employees"
          >
            Archived ({archivedEmployees.length})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading employees...</div>
        ) : (
          <div className="bg-md3-surface-container rounded-md3-lg shadow">
            <Table className="bg-md3-surface-container rounded-md3-md">
              <TableHeader>
                <TableRow>
                  <TableHead>Employee ID</TableHead>
                  <TableHead>First Name</TableHead>
                  <TableHead>Last Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Manager</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Gender</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(activeTab === "active" ? employees : archivedEmployees).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-md3-on-surface-variant">
                      {activeTab === "active" ? 'No active employees found.' : 'No archived employees.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  (activeTab === "active" ? employees : archivedEmployees).map((employee) => {
                    const employeeTeam = teams.find(t => t.id === employee.team_id);
                    const manager = employees.find(emp => emp.employee_id === employee.manager_id);
                    const statusColor = {
                      "Current": "bg-green-100 text-green-800",
                      "Resigned": "bg-yellow-100 text-yellow-800",
                      "Absconded": "bg-red-100 text-red-800",
                      "Released": "bg-gray-100 text-gray-700"
                    }[employee.employment_status || "Current"] || "bg-gray-100 text-gray-700";
                    return (
                      <TableRow key={employee.id}>
                        <TableCell className="font-medium">{employee.employee_id}</TableCell>
                        <TableCell>{employee.first_name}</TableCell>
                        <TableCell>{employee.last_name}</TableCell>
                        <TableCell>{employee.email || "-"}</TableCell>
                        <TableCell>{employee.department || "-"}</TableCell>
                        <TableCell>{employee.branch || "-"}</TableCell>
                        <TableCell>
                          {manager ? `${manager.first_name} ${manager.last_name} (${manager.employee_id})` : "-"}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                            {employee.employment_status || "Current"}
                          </span>
                        </TableCell>
                        <TableCell>{employee.gender || "-"}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" size="sm"
                             onClick={() => handleEdit(employee)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          {activeTab === "active" && (
                            <Button variant="destructive" className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" size="sm"
                               onClick={() => handleDelete(employee.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editMode ? "Edit Employee" : "Add Employee"}</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {editMode ? "Update employee information" : "Create a new employee record"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="employee_id" className="text-md3-label-large text-gray-700 font-medium">Employee ID *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="employee_id"
                  value={formData.employee_id}
                  onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                  required
                  placeholder="e.g., ME001"
                />
              </div>

              {formData.intern_id && (
                <div className="space-y-2 bg-blue-50 border border-blue-200 rounded-md3-lg p-3">
                  <Label className="text-blue-900 font-semibold text-md3-label-large text-gray-700 font-medium">Converted from Intern</Label>
                  {internDetails ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-md3-body-small font-mono bg-blue-600 text-white px-3 py-1 rounded font-semibold">
                          {internDetails.intern_number}
                        </span>
                        <span className="text-md3-body-small font-medium text-blue-900">
                          {internDetails.first_name} {internDetails.last_name}
                        </span>
                      </div>
                      <p className="text-xs text-blue-600">
                        This employee was converted from an intern • Batch: {internDetails.batch_number} • Branch: {internDetails.branch}
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-blue-600">Loading intern details...</span>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-md3-label-large text-gray-700 font-medium">First Name *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  placeholder="Enter first name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-md3-label-large text-gray-700 font-medium">Last Name *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  placeholder="Enter last name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pseudo_first_name" className="text-md3-label-large text-gray-700 font-medium">Pseudo First Name (Optional)</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="pseudo_first_name"
                  value={formData.pseudo_first_name || ""}
                  onChange={(e) => setFormData({ ...formData, pseudo_first_name: e.target.value })}
                  placeholder="Enter pseudo first name (if needed)"
                />
                <p className="text-xs text-md3-on-surface-variant">Used for user account creation if provided</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pseudo_last_name" className="text-md3-label-large text-gray-700 font-medium">Pseudo Last Name (Optional)</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="pseudo_last_name"
                  value={formData.pseudo_last_name || ""}
                  onChange={(e) => setFormData({ ...formData, pseudo_last_name: e.target.value })}
                  placeholder="Enter pseudo last name (if needed)"
                />
                <p className="text-xs text-md3-on-surface-variant">Used for user account creation if provided</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="department" className="text-md3-label-large text-gray-700 font-medium">Department *</Label>
                <Select
                  value={formData.department}
                  onValueChange={handleDepartmentChange}
                >
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="access_level" className="text-md3-label-large text-gray-700 font-medium">Access Level *</Label>
                <Select
                  value={formData.access_level}
                  onValueChange={(value) => setFormData({ ...formData, access_level: value })}
                >
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Team Member">Team Member</SelectItem>
                    <SelectItem value="Team Lead">Team Lead</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Admin">Admin</SelectItem>
                    {formData.department === "Management" && (
                      <SelectItem value="Management">Management</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {formData.department === "Management" && (
                  <p className="text-xs text-md3-on-surface-variant">Management access level is only available for Management department</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="branch" className="text-md3-label-large text-gray-700 font-medium">
                  Branch {formData.department !== "Management" && "*"}
                </Label>
                <Select
                  value={formData.branch}
                  onValueChange={(value) => setFormData({ ...formData, branch: value })}
                  disabled={formData.department === "Management"}
                >
                  <SelectTrigger className={formData.department === "Management" ? "bg-md3-surface-container" : ""}>
                    <SelectValue placeholder={formData.department === "Management" ? "Not applicable for Management" : "Select branch"} />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.name}>{b.name} ({b.country})</SelectItem>
                    ))}
                    <SelectItem value="Vendor">Vendor</SelectItem>
                  </SelectContent>
                </Select>
                {formData.department === "Management" && (
                  <p className="text-xs text-md3-on-surface-variant">Branch field is disabled for Management department</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="team_id" className="text-md3-label-large text-gray-700 font-medium">Team (Optional)</Label>
                <Select
                  value={formData.team_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, team_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-</SelectItem>
                    {teams
                      .filter(team => team.department === formData.department)
                      .map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.team_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {teams.filter(team => team.department === formData.department).length === 0 && (
                  <p className="text-xs text-md3-on-surface-variant">No teams available for {formData.department} department</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="dob" className="text-md3-label-large text-gray-700 font-medium">Date of Birth *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="dob"
                  type="date"
                  value={formData.dob}
                  onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-md3-label-large text-gray-700 font-medium">Email</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email address"
                />
                <p className="text-xs text-md3-on-surface-variant">Used for leave notifications</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="gender" className="text-md3-label-large text-gray-700 font-medium">Gender</Label>
                <Select
                  value={formData.gender || "not-specified"}
                  onValueChange={(value) => setFormData({ ...formData, gender: value === "not-specified" ? "" : value })}
                >
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not-specified">Not Specified</SelectItem>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-md3-on-surface-variant">Required for maternity leave eligibility</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="joining_date" className="text-md3-label-large text-gray-700 font-medium">Joining Date</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="joining_date"
                  type="date"
                  value={formData.joining_date || ""}
                  onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })}
                />
                <p className="text-xs text-md3-on-surface-variant">Used for prorated leave accrual calculation</p>
              </div>

              {/* Employment Status Section */}
              <div className="space-y-4 p-4 border rounded-md3-lg bg-gray-50">
                <Label className="text-md3-label-large text-gray-700 font-semibold">Employment Status</Label>
                <div className="space-y-2">
                  <Select
                    value={formData.employment_status || "Current"}
                    onValueChange={(value) => setFormData({ ...formData, employment_status: value })}
                  >
                    <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20" data-testid="employment-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Current">Current</SelectItem>
                      <SelectItem value="Resigned">Resigned</SelectItem>
                      <SelectItem value="Absconded">Absconded</SelectItem>
                      <SelectItem value="Released">Released</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(formData.employment_status === "Resigned" || formData.employment_status === "Absconded") && (
                  <div className="space-y-2">
                    <Label className="text-md3-label-large text-gray-700 font-medium">
                      {formData.employment_status === "Resigned" ? "Resignation Date" : "Absconding Date"}
                    </Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20" type="date"
                      value={formData.resignation_date || ""}
                      onChange={(e) => setFormData({ ...formData, resignation_date: e.target.value })}
                      data-testid="resignation-date-input"
                    />
                  </div>
                )}
                {(formData.employment_status === "Resigned" || formData.employment_status === "Released") && (
                  <div className="space-y-2">
                    <Label className="text-md3-label-large text-gray-700 font-medium">Last Working Day / Release Date</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20" type="date"
                      value={formData.last_working_day || ""}
                      onChange={(e) => setFormData({ ...formData, last_working_day: e.target.value })}
                      data-testid="last-working-day-input"
                    />
                    {formData.employment_status === "Resigned" && (
                      <p className="text-xs text-yellow-700 bg-yellow-50 p-2 rounded">
                        Note: Employees with "Resigned" status can only apply for Sick Leave during notice period. Setting status to "Released" will move them to the Archived tab.
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="manager_id" className="text-md3-label-large text-gray-700 font-medium">Manager (Optional)</Label>
                <Select
                  value={formData.manager_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, manager_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="Select manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Manager</SelectItem>
                    {employees
                      .filter(emp => 
                        // Don't show the employee being edited as their own manager
                        emp.id !== editingEmployeeId && 
                        // Only show employees with manager/admin access levels
                        (emp.access_level === "Manager" || emp.access_level === "Admin" || emp.access_level === "Team Lead")
                      )
                      .map((emp) => (
                        <SelectItem key={emp.id} value={emp.employee_id}>
                          {emp.first_name} {emp.last_name} ({emp.employee_id})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-md3-on-surface-variant">Manager who will approve leave requests</p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" className="flex-1 border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  {editMode ? "Update Employee" : "Create Employee"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Dialog */}
        <Dialog open={bulkUploadDialogOpen} onOpenChange={setBulkUploadDialogOpen}>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Bulk Upload Employees</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Upload multiple employees at once using an Excel file
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-md3-lg">
                <p className="text-md3-body-small text-blue-800 mb-2">
                  <strong>Step 1:</strong> Download the Excel template
                </p>
                <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={handleDownloadTemplate} size="sm">
                  Download Template
                </Button>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-md3-lg">
                <p className="text-md3-body-small text-amber-800 mb-2">
                  <strong>Step 2:</strong> Fill in employee data in the template
                </p>
                <ul className="text-xs text-amber-700 list-disc list-inside space-y-1">
                  <li>Required fields: Employee ID, First Name, Last Name, Department, DOB</li>
                  <li>Date format: YYYY-MM-DD (e.g., 1990-01-15)</li>
                  <li>Valid departments: Management, Sales, Estimation, Projects, PMO, Training, Human Resources, Accounts and Finance, Quality Assurance</li>
                  <li>Valid access levels: Team Member, Team Lead, Manager, Admin</li>
                  <li>Valid branches: Hyderabad, Chennai, Vendor (or leave empty)</li>
                  <li>Pseudo names and email are optional</li>
                </ul>
              </div>

              <div className="p-4 bg-green-50 border border-green-200 rounded-md3-lg">
                <p className="text-md3-body-small text-green-800 mb-2">
                  <strong>Step 3:</strong> Upload the completed Excel file
                </p>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 mb-2" type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  disabled={uploading}
                  
                />
                {selectedFile && (
                  <p className="text-xs text-md3-on-surface-variant">Selected: {selectedFile.name}</p>
                )}
              </div>

              {uploadResult && (
                <div className={`p-4 border rounded-md3-lg ${
                  uploadResult.error_count === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                }`}>
                  <p className="font-semibold mb-2">{uploadResult.message}</p>
                  <div className="text-md3-body-small space-y-1">
                    <p>✅ Successfully created: {uploadResult.success_count}</p>
                    <p>❌ Errors: {uploadResult.error_count}</p>
                  </div>
                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div className="mt-3">
                      <p className="text-md3-body-small font-semibold mb-1">Error Details:</p>
                      <ul className="text-xs space-y-1">
                        {uploadResult.errors.map((error, idx) => (
                          <li key={idx} className="text-red-700">• {error}</li>
                        ))}
                      </ul>
                      {uploadResult.error_count > 10 && (
                        <p className="text-xs text-md3-on-surface-variant mt-2">
                          Showing first 10 errors. Total errors: {uploadResult.error_count}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setBulkUploadDialogOpen(false);
                    setSelectedFile(null);
                    setUploadResult(null);
                  }} 
                  className="flex-1"
                  disabled={uploading}
                >
                  Close
                </Button>
                <Button 
                  onClick={handleBulkUpload} 
                  className="flex-1"
                  disabled={!selectedFile || uploading}
                >
                  {uploading ? "Uploading..." : "Upload Employees"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Import Preview Dialog */}
        <BulkImportPreview
          open={importPreviewOpen}
          onClose={() => setImportPreviewOpen(false)}
          previewData={importPreviewData}
          onApply={handleApplyImport}
          entityType="Employees"
        />
      </div>
    </Layout>
  );
}
